import React, { useState, useCallback } from 'react';
import { RGBColor } from 'react-color';
import HBPopover from '../HBPopover/HBPopover';
import { HBColorPickerWithHoc } from './HBColorPicker';
import ColorCircleBlock from './ColorCircleBlock';
import useColorPickerConverter from './useColorPickerConverter';
import { useBuildTestIdInternal } from '../../hooks/useBuildTestIdInternal';
import { MustIncludeOneOfBasicTestIdProps } from '../common/internal/test-id.types';
import { Z_INDEX } from '../../styles/variables';
import HBColorPickerDrawer from './HBColorPickerDrawer/HBColorPickerDrawer';
import HBClickable from '../HBClickable/HBClickable';
import { useHBTranslation } from '../HBUIProvider/HBUIProvider';

export interface IColorPickerButton {
    disableAlpha?: boolean;
    selectedColor?: string;
    removeTransparent?: boolean;
    presetColors?: RGBColor[] | string[];
    className?: string;
    onChange?: (color: string) => void;
    withActions?: boolean;
    targetContainer?: React.ReactNode;
    presetColorsTitle?: string;
    shouldSupportOverflowHidden?: boolean;
    stopPropagationOnTargetClick?: boolean;
    stopPropagationOnColorPickerClick?: boolean;
    showInDrawer?: boolean;
    onCancel?: () => void;
}

const HBColorPickerButton = ({
    testIdName,
    testId,
    disableAlpha,
    presetColors,
    className,
    onChange,
    withActions = false,
    selectedColor,
    removeTransparent,
    targetContainer,
    presetColorsTitle: presetColorsTitleProp,
    shouldSupportOverflowHidden,
    stopPropagationOnTargetClick,
    stopPropagationOnColorPickerClick,
    showInDrawer = false,
    onCancel
}: IColorPickerButton & MustIncludeOneOfBasicTestIdProps) => {
    const t = useHBTranslation();
    const presetColorsTitle =
        presetColorsTitleProp ?? t('HBColorPicker.presetColorsTitle');
    const [currentColor, setCurrentColor] = useState<string>(selectedColor);
    const [forceClose, setForceClose] = useState(false);
    const [isOpenDrawer, setIsOpenDrawer] = useState(false);

    const effectiveWithActions = showInDrawer || withActions;

    const getEffectiveColor = () => {
        return effectiveWithActions ? currentColor : selectedColor;
    };

    const handleColorChange = (color: string) => {
        if (effectiveWithActions) {
            setCurrentColor(color);
        } else {
            onChange?.(color);
        }
    };

    const { onChange: _onChange, color } = useColorPickerConverter(
        getEffectiveColor(),
        handleColorChange
    );

    const handleClose = useCallback(() => {
        if (showInDrawer) {
            setIsOpenDrawer(false);
        } else {
            setForceClose(true);
        }
    }, [showInDrawer, setIsOpenDrawer, setForceClose]);

    const handleSave = useCallback(() => {
        handleClose();
        if (onChange) {
            onChange(currentColor);
        }
    }, [currentColor, onChange, handleClose]);

    const handleDrawerOpen = () => {
        setIsOpenDrawer(true);
    };

    const handleCancel = () => {
        handleClose();

        if (effectiveWithActions) {
            // support for when no targetContainer is provided
            setCurrentColor(selectedColor);
        }

        if (onCancel) {
            onCancel();
        }
    };

    const [shouldBeSelected, setShouldBeSelected] = useState(false);

    const finalTestId = useBuildTestIdInternal({
        testId,
        identifier: testIdName,
        suffix: 'color-picker',
        componentName: 'HBColorPickerButton'
    });

    if (showInDrawer) {
        return (
            <>
                {targetContainer ? (
                    <HBClickable
                        testIdName={`${finalTestId}-button`}
                        onClick={handleDrawerOpen}
                    >
                        {targetContainer}
                    </HBClickable>
                ) : (
                    <ColorCircleBlock
                        testId={finalTestId}
                        color={color?.rgb}
                        alpha={color?.rgb?.a}
                        onClick={handleDrawerOpen}
                        isSelected={isOpenDrawer}
                    />
                )}
                <HBColorPickerDrawer
                    testId={finalTestId}
                    isOpen={isOpenDrawer}
                    onClose={handleCancel}
                    onSave={handleSave}
                    title={presetColorsTitle}
                >
                    <HBColorPickerWithHoc
                        showInDrawer
                        disableAlpha
                        removeTransparent
                        onChange={_onChange}
                        selectedColor={color}
                        presetColors={presetColors}
                        className={className}
                        stopPropagationOnColorPickerClick
                    />
                </HBColorPickerDrawer>
            </>
        );
    }

    return (
        <HBPopover
            target={
                targetContainer || (
                    <ColorCircleBlock
                        testId={finalTestId}
                        color={color?.rgb}
                        alpha={color?.rgb?.a}
                        isSelected={shouldBeSelected}
                    />
                )
            }
            openTrigger="click"
            onOpenChange={({ isOpen }) => {
                setShouldBeSelected(isOpen);
                setForceClose(false);
            }}
            shouldSupportClickOutside
            shouldSupportCloseOnEnter
            shouldSupportOverflowHidden={shouldSupportOverflowHidden}
            stopPropagationOnTargetClick={stopPropagationOnTargetClick}
            customStyle={{
                padding: 0,
                cursor: 'default',
                zIndex: Z_INDEX.POPOVER
            }}
            forceClose={forceClose}
            testId="storybook"
        >
            <HBColorPickerWithHoc
                onChange={_onChange}
                onSaveClicked={withActions ? handleSave : undefined}
                onCancelClicked={withActions ? handleCancel : undefined}
                selectedColor={color}
                disableAlpha={disableAlpha}
                presetColors={presetColors}
                className={className}
                removeTransparent={removeTransparent}
                presetColorsTitle={presetColorsTitle}
                stopPropagationOnColorPickerClick={
                    stopPropagationOnColorPickerClick
                }
            />
        </HBPopover>
    );
};

export default HBColorPickerButton;
